import type { AvailabilityTier, SkuAvailabilityRow } from '@sifly/shared-types'

export type ResolvedAvailability = {
  tier: AvailabilityTier
  label: string | null
  canPurchase: boolean
}

/**
 * Customer-facing availability tier resolver. Pure; consumed by portal
 * (tier preview on SKU detail) AND storefront (PDP + product card badge).
 *
 * Tier priority: untracked → out-of-stock-by-active → local → usa → bulgaria.
 * Anything tracked with all pools empty (and active=true) is also out-of-stock.
 *
 * Labels match the design review's rewrite of the original spec:
 *   - "Same-day pickup or ships in 1–2 days"   (dealer_local > 0)
 *   - "Usually ships in 2–3 days"             (sifly_usa > 0)
 *   - "Ships in 3–5 weeks"                    (sifly_bulgaria > 0)
 *   - "Currently unavailable"                 (out of stock, blocks add-to-cart)
 */
export function resolveAvailabilityTier(row: SkuAvailabilityRow): ResolvedAvailability {
  if (!row.track_inventory) {
    return { tier: 'untracked', label: null, canPurchase: true }
  }
  if (!row.is_active) {
    return { tier: 'out_of_stock', label: 'Currently unavailable', canPurchase: false }
  }
  if (row.dealer_local_qty > 0) {
    return { tier: 'local', label: 'Same-day pickup or ships in 1–2 days', canPurchase: true }
  }
  if (row.usa_qty > 0) {
    return { tier: 'usa', label: 'Usually ships in 2–3 days', canPurchase: true }
  }
  if (row.bulgaria_qty > 0) {
    return { tier: 'bulgaria', label: 'Ships in 3–5 weeks', canPurchase: true }
  }
  return { tier: 'out_of_stock', label: 'Currently unavailable', canPurchase: false }
}

/**
 * Aggregate availability for a bundle — the customer experience of a bundle
 * line is bottlenecked by its slowest-shipping component. We take the worst
 * tier across the provided rows. Untracked components are ignored (they
 * don't gate the bundle); a bundle entirely composed of untracked SKUs is
 * itself reported as untracked (no badge).
 *
 * Ordering, worst → best:
 *   out_of_stock > bulgaria > usa > local
 */
const TIER_RANK: Record<AvailabilityTier, number> = {
  out_of_stock: 4,
  bulgaria: 3,
  usa: 2,
  local: 1,
  untracked: 0,
}

export function resolveBundleAvailability(rows: SkuAvailabilityRow[]): ResolvedAvailability {
  if (rows.length === 0) {
    return { tier: 'untracked', label: null, canPurchase: true }
  }
  let worst: ResolvedAvailability = { tier: 'untracked', label: null, canPurchase: true }
  for (const row of rows) {
    const r = resolveAvailabilityTier(row)
    if (r.tier === 'untracked') continue
    if (TIER_RANK[r.tier] > TIER_RANK[worst.tier]) {
      worst = r
    }
  }
  return worst
}

/**
 * Tailwind tokens for the badge rendering. Kept here so portal + storefront
 * agree on visual treatment. Color is not the only differentiator (icon and
 * text label carry the meaning) — these tones are accents.
 */
export const AVAILABILITY_TIER_STYLES: Record<AvailabilityTier, { className: string; ariaLabel: string }> = {
  local:        { className: 'bg-green-50  text-green-700  border-green-200',  ariaLabel: 'Same-day pickup or ships in 1 to 2 days' },
  usa:          { className: 'bg-blue-50   text-blue-700   border-blue-200',   ariaLabel: 'Usually ships in 2 to 3 days' },
  bulgaria:     { className: 'bg-amber-50  text-amber-700  border-amber-200',  ariaLabel: 'Ships in 3 to 5 weeks' },
  out_of_stock: { className: 'bg-gray-100  text-gray-600   border-gray-300',   ariaLabel: 'Currently unavailable' },
  untracked:    { className: '',                                                ariaLabel: '' },
}
